import { getFeatureToggle } from "@blend-ed/blendx-platform/utils";
import { Button, SwitchToggle, useMediaQuery } from "@blend-ed/blendx-ui";
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import CarouselContainer from "../../components/CarouselContainer";
import ProgressOverviewComponent from "../../components/ProgressOverviewComponent";
import AiHome from './AiHome';
import messages from "./messages";
import RecommendedCourses from "./recommended-courses";
import RecommendedPrograms from "./recommended-programs";
import Updates from "./updates";
import LearningView from "./ai-home/LearningView";

const Home = ({ intl }) => {
    const [homeToggle, setHomeToggle] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const [learningView, setLearningView] = useState(false);
    const [generatedLearningPath, setGeneratedLearningPath] = useState(null);
    const { features_ai_chat, features_enable_ai_home, features_ai_home_page } = getFeatureToggle();
    const isTablet = useMediaQuery({ query: '(min-width: 768px)' });
    const isAiHomePageEnabled = isTablet && features_ai_home_page && features_ai_chat && features_enable_ai_home;

    // Set initial view based on URL hash
    useEffect(() => {
        // Only run this effect on initial mount
        const hash = location.hash.toLowerCase();
        if (hash === '#aihome') {
            setHomeToggle(false);
        } else {
            // Default to regular home
            setHomeToggle(true);
        }
    }, []);  // Empty dependency array so it only runs once on mount

    // Update URL hash when view changes
    const handleViewChange = (isLegacy) => {
        setHomeToggle(isLegacy);
        if (isLegacy) {
            // Going to home view
            // Just update the state without changing the URL to avoid navigation loops
            setHomeToggle(true);
        } else {
            // Going to AI home view
            // Just update the state without changing the URL to avoid navigation loops
            setHomeToggle(false);
        }
    };

    const handleCloseLearningView = () => {
        setLearningView(false);
    };

    const handleLearnerLearningPath = (learningPath) => {
        setGeneratedLearningPath(learningPath);
    };

    if (learningView) {
        return <LearningView learningPath={generatedLearningPath} setLearningView={setLearningView} onClose={handleCloseLearningView} intl={intl} />
    }

    return (
        <main>
            {isAiHomePageEnabled && (
                <div className="home-toggle-container">
                    <SwitchToggle name="home-toggle" value={homeToggle ? "home" : "aiHome"}>
                        <SwitchToggle.Button
                            value="home"
                            onClick={() => handleViewChange(true)}
                        >
                            {intl.formatMessage(messages.homeView)}
                        </SwitchToggle.Button>
                        <SwitchToggle.Button
                            value="aiHome"
                            onClick={() => handleViewChange(false)}
                        >
                            {intl.formatMessage(messages.aiHomeView)}
                        </SwitchToggle.Button>
                    </SwitchToggle>
                </div>
            )}
            {homeToggle || !isAiHomePageEnabled ? (
                <LegacyHome intl={intl} />
            ) : (
                <AiHome
                    intl={intl}
                    expandedView={learningView}
                    setExpandedView={setLearningView}
                    handleLearnerLearningPath={handleLearnerLearningPath}
                />
            )}
        </main>
    );
};

function LegacyHome({ intl }) {
    const navigate = useNavigate();
    const isTablet = useMediaQuery({ query: '(min-width: 768px)' });
    return (
        <>
            {isTablet && <CarouselContainer />}
            <div className="progress-card-container">
                <div className="progress-card__header">
                    <div className="progress-card__title">{intl.formatMessage(messages.progressTitle)}</div>
                    {isTablet && <Button size="xs" variant="ghost-gray" onClick={() => navigate('progress')}>
                        {intl.formatMessage(messages.seeProgress)}
                    </Button>}
                </div>
                <ProgressOverviewComponent />
            </div>
            <Updates />
            <RecommendedCourses />
            <RecommendedPrograms compact={true} />
        </>
    );
};
Home.propTypes = {
    intl: intlShape.isRequired,
};

export default injectIntl(Home);